import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { I18nextProvider } from "react-i18next";
import { Toaster } from "sonner";
import { MapPin } from "lucide-react";
import { AdProvider, useAdConfig, SplashScreen } from "@tabipla/components";
import i18n from "./infrastructure/i18n";
import { HomePage } from "./presentation/pages/Home";
import { LandingPage } from "./presentation/pages/Landing";
import { SpotDetailPage } from "./presentation/pages/SpotDetail";
import { SpotFormPage } from "./presentation/pages/SpotForm";
import { BookletsPage } from "./presentation/pages/Booklets";
import { BookletDetailPage } from "./presentation/pages/BookletDetail";
import { BookletFormPage } from "./presentation/pages/BookletForm";
import { SharedBookletPage } from "./presentation/pages/SharedBooklet";
import { DiscoverPage } from "./presentation/pages/Discover";
import { PublicBookletDetailPage } from "./presentation/pages/PublicBookletDetail";
import { UserProfilePage } from "./presentation/pages/UserProfile";
import { UserFollowersPage } from "./presentation/pages/UserFollowers";
import { UserFollowingPage } from "./presentation/pages/UserFollowing";
import { PublicSpotDetailPage } from "./presentation/pages/PublicSpotDetail";
import { ProfilePage } from "./presentation/pages/Profile";
import { SettingsPage } from "./presentation/pages/Settings";
import { LoginPage } from "./presentation/pages/Login";
import { RegisterPage } from "./presentation/pages/Register";
import { useAuth } from "./application/hooks/useAuth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache for persistence
      retry: 1,
    },
  },
});

// Persist query cache to localStorage for offline support
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: "mekke-query-cache",
});

// Mekke テーマ
const mekkeSplashTheme = {
  backgroundGradient: "from-rose-50 to-white dark:from-rose-950 dark:to-gray-900",
  iconBg: "bg-rose-500",
  iconText: "text-white",
  progressColor: "bg-rose-500",
};

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Shows Landing for unauthenticated users, HomePage for authenticated users
function ConditionalHomeRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <HomePage /> : <LandingPage />;
}

function AppWithAds({ children }: { children: React.ReactNode }) {
  const adConfig = useAdConfig({
    publisherId: import.meta.env.VITE_ADSENSE_PUBLISHER_ID,
    enabled: import.meta.env.VITE_ADS_ENABLED,
    testMode: import.meta.env.VITE_ADS_TEST_MODE,
  });

  return <AdProvider config={adConfig}>{children}</AdProvider>;
}

function AppContent() {
  const { isLoading } = useAuth();

  return (
    <SplashScreen
      appName="Mekke"
      tagline="お気に入りの場所を、記録しよう"
      icon={<MapPin className="h-8 w-8" />}
      theme={mekkeSplashTheme}
      isLoading={isLoading}
    >
      <Routes>
        {/* Home Route - Shows Landing or HomePage based on auth */}
        <Route path="/" element={<ConditionalHomeRoute />} />

        {/* Protected Routes */}
        <Route
          path="/spots/new"
          element={
            <ProtectedRoute>
              <SpotFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/spots/:id"
          element={
            <ProtectedRoute>
              <SpotDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/spots/:id/edit"
          element={
            <ProtectedRoute>
              <SpotFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/booklets"
          element={
            <ProtectedRoute>
              <BookletsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/booklets/new"
          element={
            <ProtectedRoute>
              <BookletFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/booklets/:id"
          element={
            <ProtectedRoute>
              <BookletDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/booklets/:id/edit"
          element={
            <ProtectedRoute>
              <BookletFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Shared Routes (accessible to anyone) */}
        <Route path="/shared/:shareCode" element={<SharedBookletPage />} />

        {/* Discover Routes (accessible to anyone, but follow requires auth) */}
        <Route path="/discover" element={<DiscoverPage />} />
        <Route path="/booklets/public/:bookletId/:spotId" element={<PublicSpotDetailPage />} />
        <Route path="/booklets/public/:id" element={<PublicBookletDetailPage />} />
        <Route path="/users/:userId" element={<UserProfilePage />} />
        <Route path="/users/:userId/followers" element={<UserFollowersPage />} />
        <Route path="/users/:userId/following" element={<UserFollowingPage />} />
      </Routes>
    </SplashScreen>
  );
}

export function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <AppWithAds>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{
            persister,
            maxAge: 1000 * 60 * 60 * 24, // 24 hours
            dehydrateOptions: {
              shouldDehydrateQuery: (query) => {
                // Only persist successful queries for spots and booklets
                const queryKey = query.queryKey[0];
                if (typeof queryKey !== "string") return false;
                const persistableKeys = ["spots", "spot", "booklets", "booklet"];
                return (
                  query.state.status === "success" &&
                  persistableKeys.some((key) => queryKey.startsWith(key))
                );
              },
            },
          }}
        >
          <Toaster
            position="top-center"
            toastOptions={{
              className: "font-sans",
              style: {
                padding: "12px 16px",
                borderRadius: "12px",
              },
            }}
            richColors
            closeButton
          />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </PersistQueryClientProvider>
      </AppWithAds>
    </I18nextProvider>
  );
}
